/**
 * InitiativeSpecPanel
 *
 * What intake produces: the assembled initiative spec. This is where the
 * review panel (FLOW-549) mounts once breakdown generation (FLOW-545) exists;
 * until then the spec itself is the honest end of the flow.
 *
 * Decisions the model assumed are marked — the intake engine flags them so the
 * riskiest parts of a plan are never mistaken for something the user decided.
 */

import { Component, For, JSX, Show } from "solid-js";
import type { InitiativeSpec } from "../../api/types";

export interface InitiativeSpecPanelProps {
  spec: InitiativeSpec;
}

export const InitiativeSpecPanel: Component<InitiativeSpecPanelProps> = (
  props,
) => (
  <section
    class="rounded-md border border-line bg-surface p-6"
    aria-labelledby="spec-title"
  >
    <p class="inline-flex items-center gap-1.5 text-xs font-medium text-signal-green">
      <CheckIcon />
      Intake complete
    </p>

    <h2 id="spec-title" class="mt-3 text-lg font-semibold text-ink">
      {props.spec.title}
    </h2>

    <Section title="Scope">
      <p class="text-sm text-ink-2 whitespace-pre-wrap">{props.spec.scope}</p>
    </Section>

    <Show when={props.spec.known_constraints.length > 0}>
      <Section title="Constraints">
        <ul class="flex flex-col gap-1.5">
          <For each={props.spec.known_constraints}>
            {(constraint) => (
              <li class="text-sm text-ink-2 flex gap-2">
                <span class="text-ink-3" aria-hidden="true">
                  •
                </span>
                {constraint}
              </li>
            )}
          </For>
        </ul>
      </Section>
    </Show>

    <Show when={props.spec.open_decisions.length > 0}>
      <Section title="Open decisions">
        <ul class="flex flex-col gap-3">
          <For each={props.spec.open_decisions}>
            {(decision) => (
              <li class="text-sm">
                <p class="text-ink">{decision.question}</p>
                <Show
                  when={decision.resolution}
                  fallback={<p class="mt-0.5 text-ink-3 italic">Unresolved</p>}
                >
                  <p class="mt-0.5 text-ink-2">{decision.resolution}</p>
                </Show>
                <Show when={decision.is_assumption}>
                  <span class="mt-1 inline-flex items-center rounded bg-signal-amber-tint px-1.5 py-0.5 text-xs font-medium text-signal-amber">
                    Assumed
                  </span>
                </Show>
              </li>
            )}
          </For>
        </ul>
      </Section>
    </Show>

    <Show when={props.spec.key_context.length > 0}>
      <Section title="Key context">
        <ul class="flex flex-col gap-1.5">
          <For each={props.spec.key_context}>
            {(context) => (
              <li class="text-sm text-ink-2 flex gap-2">
                <span class="text-ink-3" aria-hidden="true">
                  •
                </span>
                {context}
              </li>
            )}
          </For>
        </ul>
      </Section>
    </Show>
  </section>
);

const Section: Component<{ title: string; children: JSX.Element }> = (
  props,
) => (
  <div class="mt-5">
    <h3 class="text-xs font-semibold uppercase tracking-wide text-ink-3">
      {props.title}
    </h3>
    <div class="mt-2">{props.children}</div>
  </div>
);

const CheckIcon: Component = () => (
  <svg
    class="w-4 h-4"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      stroke-linecap="round"
      stroke-linejoin="round"
      stroke-width="2"
      d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

export default InitiativeSpecPanel;
